<script setup lang="ts">
const { modalType, isModalOpen } = storeToRefs(useLayoutStore());
const { closeModal } = useLayoutStore();
const { parameters } = useParameters();
const { t } = useTexts();

// fecha o modal do Minha Conta V2 ao navegar pra fora dele (mesmo padrão de
// BonusWallet.vue) — sem isso o middleware userV2Modal só reage a rotas
// /user/*, então ir pra /games deixava o modal aberto por cima da página nova.
const { close: closeUserV2Modal } = useUserV2Modal();

const isOpen = computed(
  () => isModalOpen.value && modalType.value === 'bonus-rules',
);

const modalCardRef = ref<HTMLElement | null>(null);

const { trapFocus } = useModalA11y(modalCardRef, isOpen, closeModal);

// mesmas fontes de dado do modal antigo (useParameters) — só o texto ao
// redor do número mudou no redesenho, a regra de negócio é a mesma.
const casinoRolloverMultiplier = computed(
  () => parameters.value?.casino?.rollOverMultiplier ?? 0,
);

const casinoExpiresInHours = computed(
  () => parameters.value?.rollover_casino_expires_in ?? 0,
);

const maxWithdrawalMultiplier = computed(
  () => parameters.value?.qtd_withdrawal_bonus || 1,
);

const STATS = computed(() => [
  {
    key: 'casino-bets',
    icon: getAssetsUrlForCDN('/images/user-v2/bonus-rules/fire.svg'),
    label: t('account.wallet.bonus_rules_modal.casino_bets'),
    labelFull: t('account.wallet.bonus_rules_modal.casino_bets_full'),
    value: t('account.wallet.bonus_rules_modal.casino_bets_value_short', {
      n: casinoRolloverMultiplier.value,
    }),
    valueFull: t('account.wallet.bonus_rules_modal.casino_bets_value', {
      n: casinoRolloverMultiplier.value,
    }),
  },
  {
    key: 'expiry',
    icon: getAssetsUrlForCDN('/images/user-v2/bonus-rules/hourglass.svg'),
    label: t('account.wallet.bonus_rules_modal.casino_expiry_time'),
    labelFull: t('account.wallet.bonus_rules_modal.casino_expiry_time_full'),
    value: `${casinoExpiresInHours.value} ${t('account.wallet.bonus_rules_modal.hour')}`,
    valueFull: `${casinoExpiresInHours.value} ${t('account.wallet.bonus_rules_modal.hour')}`,
  },
  {
    key: 'max-withdrawal',
    icon: getAssetsUrlForCDN('/images/user-v2/bonus-rules/dollar-sign.svg'),
    label: t('account.wallet.bonus_rules_modal.max_bonus_withdrawal'),
    labelFull: t('account.wallet.bonus_rules_modal.max_bonus_withdrawal_full'),
    value: t(
      'account.wallet.bonus_rules_modal.max_bonus_withdrawal_value_short',
      { n: maxWithdrawalMultiplier.value },
    ),
    valueFull: t(
      'account.wallet.bonus_rules_modal.max_bonus_withdrawal_value',
      { n: maxWithdrawalMultiplier.value },
    ),
  },
]);
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <div
        v-if="isOpen"
        class="user-wallet-bonus-rules-modal fixed inset-0 z-[1000] flex items-end justify-center font-sans lg:items-center lg:p-[16px]"
        @dragstart.prevent
      >
        <!-- o clique fora cai NESTE backdrop (cobre o wrapper inteiro) -->
        <div
          class="modal-backdrop absolute inset-0 bg-black/70"
          @click="closeModal"
        />

        <div
          ref="modalCardRef"
          aria-labelledby="bonus-rules-title"
          aria-modal="true"
          class="modal-card relative flex w-full max-h-[90dvh] flex-col overflow-hidden rounded-t-[22px] bg-v2-default-200 text-left lg:max-w-[500px] lg:rounded-[22px]"
          role="dialog"
          tabindex="-1"
          @keydown="trapFocus"
        >
          <!-- X próprio do Figma (node 24087:70024) -->
          <button
            class="absolute right-[14px] top-[14px] flex size-[32px] items-center justify-center rounded-[8px]"
            type="button"
            @click="closeModal"
          >
            <img
              alt=""
              class="size-[18px]"
              :src="getAssetsUrlForCDN('/images/user-v2/bonus-rules/close.svg')"
            />
          </button>

          <!-- rola sozinho, sem o X junto: viewport baixo (ex.: paisagem
               mobile) não corta mais o topo do card, ver Modal.vue
               (.wrapper/.inner) pro mesmo split -->
          <div
            class="flex min-h-0 flex-col gap-[16px] overflow-y-auto p-[20px]"
          >
            <div class="flex flex-col gap-[5px] pr-[32px]">
              <h6
                id="bonus-rules-title"
                class="text-[19px] font-bold leading-[1.2] text-white"
              >
                {{ $t('account.wallet.bonus_rules_modal.title') }}
              </h6>
              <!-- cor exata do Figma — sem token equivalente em
                 user-v2-tokens.scss, mesma convenção do gradiente em
                 CoinsCard.vue -->
              <p class="text-[14px] font-medium leading-[1.4] text-[#d4d9e0]">
                {{ $t('account.wallet.bonus_rules_modal.subtitle') }}
              </p>
            </div>

            <div class="flex flex-col gap-[8px] md:flex-row md:gap-[6px]">
              <div
                v-for="stat in STATS"
                :key="stat.key"
                class="flex shrink-0 items-center gap-[12px] rounded-[12px] bg-white/[.04] px-[10px] py-[10px] md:gap-[6px] md:py-[6px]"
              >
                <img
                  alt=""
                  class="size-[32px] shrink-0"
                  :src="stat.icon"
                />
                <div
                  class="flex flex-col gap-[2px] whitespace-nowrap md:items-center"
                >
                  <span class="text-[12px] font-semibold text-white md:hidden">
                    {{ stat.labelFull }}
                  </span>
                  <span
                    class="hidden text-[11px] font-normal text-white/60 md:inline"
                  >
                    {{ stat.label }}
                  </span>
                  <span class="text-[11px] font-medium text-white/80 md:hidden">
                    {{ stat.valueFull }}
                  </span>
                  <span
                    class="hidden text-[11px] font-semibold text-white md:inline"
                  >
                    {{ stat.value }}
                  </span>
                </div>
              </div>
            </div>

            <!-- mesma cor #d4d9e0 do subtítulo acima, sem token equivalente -->
            <p class="text-[14px] font-medium leading-[1.4] text-[#d4d9e0]">
              {{
                $t('account.wallet.bonus_rules_modal.bonus_withdrawal_rules')
              }}
            </p>

            <div class="flex flex-col">
              <div class="flex items-center gap-[11px] py-[14px]">
                <div
                  class="flex size-[30px] shrink-0 items-center justify-center rounded-full bg-white/10"
                >
                  <img
                    alt=""
                    class="size-[18px]"
                    :src="
                      getAssetsUrlForCDN(
                        '/images/user-v2/bonus-rules/slot-machine.svg',
                      )
                    "
                  />
                </div>
                <div class="flex flex-1 flex-col gap-[2px]">
                  <p class="text-[14px] font-bold text-white">
                    {{ $t('account.wallet.bonus_rules_modal.casino') }}
                  </p>
                  <!-- cor exata do Figma — sem token equivalente em
                     user-v2-tokens.scss -->
                  <p class="text-[12.5px] font-medium text-[#9ba6be]">
                    {{
                      $t(
                        'account.wallet.bonus_rules_modal.bonus_withdrawal_casino_rules',
                      )
                    }}
                  </p>
                </div>
              </div>
              <div class="h-px w-full bg-white/[.08]" />
            </div>

            <NuxtLink
              class="flex h-[40px] w-full items-center justify-center rounded-full bg-v2-primary text-[17px] font-bold text-white"
              :to="
                localeLink(
                  '/games/category/all?initialCategory=jogar-com-bonus',
                )
              "
              @click="
                closeModal();
                closeUserV2Modal();
              "
            >
              {{ $t('account.wallet.bonus_rules_modal.view_games_button') }}
            </NuxtLink>

            <button
              class="flex h-[40px] w-full items-center justify-center rounded-full bg-v2-secondary text-[17px] font-bold text-white"
              type="button"
              @click="closeModal"
            >
              {{ $t('account.wallet.bonus_rules_modal.confirm_button') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </ClientOnly>
</template>

<style scoped lang="scss">
// main.scss reseta `transition` no app inteiro — animação de entrada via
// @keyframes/animation (mesma estratégia do UserV2LogoutConfirmModal.vue).
.modal-backdrop {
  animation: bonus-rules-fade 0.18s ease-out;
}

.modal-card {
  animation: bonus-rules-pop 0.2s ease-out;

  @media (max-width: 1023px) {
    animation-name: bonus-rules-slide-up;
  }
}

@keyframes bonus-rules-fade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes bonus-rules-pop {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.96);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes bonus-rules-slide-up {
  from {
    opacity: 0;
    transform: translateY(100%);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
